import { useEffect, useRef } from "react";

/**
 * Custom hook for periodic async polling with pending request protection.
 *
 * @param {Function} callback - Async function or callback to execute
 * @param {number|null} delay - Interval in milliseconds (pass null/0/false to pause/disable polling)
 * @param {Array} deps - Dependency array that restarts/resets the polling
 * @param {boolean} immediate - Whether to trigger the callback immediately upon enabling/deps change
 */
export const usePolling = (callback, delay = 5000, deps = [], immediate = false) => {
  const isPendingRef = useRef(false);
  const savedCallback = useRef(callback);

  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  useEffect(() => {
    if (!delay) return;

    let isMounted = true;

    const execute = async () => {
      if (isPendingRef.current) return;
      isPendingRef.current = true;

      try {
        await savedCallback.current?.();
      } catch (err) {
        console.error("Polling error:", err);
      } finally {
        if (isMounted) {
          isPendingRef.current = false;
        }
      }
    };

    if (immediate) {
      execute();
    }

    const interval = setInterval(execute, delay);

    return () => {
      isMounted = false;
      isPendingRef.current = false;
      clearInterval(interval);
    };
  }, [delay, immediate, ...deps]);
};

export default usePolling;
